
/**
 * Root 컴포넌트 - 애플리케이션의 루트 레이아웃
 * 전역 레이아웃 구조 및 메타데이터 관리
 */
// import { Outlet } from 'react-router-dom'; // 웹빌더에서는 라우터 미사용
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useSelector } from 'react-redux';

import { GlobalTopBanner } from './components/ui/banner/GlobalTopBanner';
import { Header } from './components/header/Header';
import { Footer } from './components/footer/Footer';
import { ThemeProvider } from './components/ui/ThemeProvider';


import './app.css';
import { useEffect } from 'react';

gsap.registerPlugin(useGSAP);

interface LayoutProps {
    children: React.ReactNode;
    withcookieData?: any;
    mainMenus?: any[];
    globalMenus?: any[];
    menus?: any[];
    company?: any;
    previewData?: any;
    editorData?: any;
    [key: string]: any;
}

export function Layout({ children, ...props }: LayoutProps) {
    // Redux에서 withcookieData 가져오기 (없으면 props에서 사용)
    const reduxWithcookieData = useSelector((state: any) => state.withcookie?.withcookieData);
    const withcookieData = props.withcookieData || reduxWithcookieData || {};
    
    // 로그인 상태 및 사용자 정보 가져오기
    const { isUserLoggedIn, isAdminLoggedIn, userInfo } = useSelector((state: any) => state.withcookie || {});
    const isLoggedIn = isUserLoggedIn || isAdminLoggedIn; // 일반 사용자 또는 관리자 로그인
    const isBusiness = userInfo?.isBusiness || false;
    
    // 스킨 정보 추출
    const skin = withcookieData?.skin || props.skin || {};
    const company = props.company || withcookieData?.company || reduxWithcookieData?.company || {};
    const companyId = skin?.companyId || company?.id;
    
    // 메뉴 데이터 처리 (flone처럼)
    let menus = [];
    const mainMenus = reduxWithcookieData?.mainMenus || [];
    const globalMenus = reduxWithcookieData?.globalMenus || [];
    
    // 1. Redux 메뉴 데이터 사용
    if (Array.isArray(mainMenus) && mainMenus.length > 0) {
        menus = [...mainMenus];
        if (Array.isArray(globalMenus)) {
            menus = [...menus, ...globalMenus];
        }
    }
    // 2. props로 받은 메뉴 데이터 사용
    else if (props.mainMenus && props.mainMenus.length > 0) {
        menus = [...props.mainMenus];
        if (props.globalMenus) {
            menus = [...menus, ...props.globalMenus];
        }
    }
    // 3. previewData의 menus 사용
    else if (props.previewData?.menus) {
        menus = props.previewData.menus;
    }
    // 4. editorData의 menus 사용
    else if (props.editorData?.menus) {
        menus = props.editorData.menus;
    }
    // 5. 기타 menus props 사용
    else if (props.menus) {
        menus = props.menus;
    }
    // 6. withcookieData의 menus 사용
    else if (withcookieData?.menus) {
        menus = withcookieData.menus;
    }
    
    // 모든 메뉴를 Gnb에 전달하고, Gnb 내부에서 필터링하도록 변경
    // (flone과 동일한 방식)
    const headerMenus = menus;
    
    // 로고 정보 (우선순위에 따라 처리)
    const logoUrl = skin?.theme?.main_logo_url || withcookieData?.skin?.theme?.main_logo_url || '';
    const logoText = 
        skin?.extra?.logo_text || 
        company?.name || 
        withcookieData?.company?.name || 
        props.previewData?.company?.name ||
        props.editorData?.company?.name ||
        'Marijour';
    
    // 푸터 정보 추출 (flone과 동일한 필드 사용)
    const companyName = skin?.extra?.company_name || company?.name || '';
    const address = skin?.address || '';
    const phone = skin?.phone || '';
    const email = skin?.email || '';
    const businessNumber = skin?.businessNumber || '';
    const onlineSalesNumber = skin?.mailOrderBusinessCertificate || '';
    const hostingProvider = 'Coderecipe Inc.';
    const ceo = skin?.owner || '';
    const privacyOfficer = skin?.privacyOfficer || skin?.owner || '';
    
    // companySettingJson 파싱 및 배너 텍스트 추출
    let bannerText = skin?.extra?.top_banner_text || 'Happy Family Month 🤍 16% Sale';
    
    // skin.company.companySettingJson을 우선적으로 확인
    const companySettingJsonString = skin?.company?.companySettingJson || company?.companySettingJson;
    
    if (companySettingJsonString) {
        try {
            const companySettings = JSON.parse(companySettingJsonString);
            if (companySettings?.banner_text) {
                bannerText = companySettings.banner_text;
            }
        } catch (e) {
            console.error('Failed to parse companySettingJson:', e);
        }
    }
    

    
    // 디버깅을 위한 로그
    const handleResize = () => {
        const bannerTextBox = document.querySelector('#globalTopBanner');
        const header = document.querySelector('#header');
        const footer = document.querySelector('#footer');
        const main = document.querySelector('#main');

        const bannerHeight = bannerTextBox?.clientHeight || 0;
        const headerHeight = header?.clientHeight || 0;
        const footerHeight = footer?.clientHeight || 0;
        const totalHeight = bannerHeight + headerHeight + footerHeight;

        gsap.set(main, { minHeight: `calc(100vh - ${totalHeight}px)` });
    };

    useEffect(() => {
        handleResize();

        // 리사이즈 이벤트 최적화 (디바운싱)
        let resizeTimeout: NodeJS.Timeout;
        const handleResizeTimeout = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                handleResize();
            }, 10);
        };

        window.addEventListener('resize', handleResizeTimeout, { passive: true });

        // cleanup 함수
        return () => {
            window.removeEventListener('resize', handleResizeTimeout);
            clearTimeout(resizeTimeout);
        };
    }, []);

    return (
        <ThemeProvider>
            <div className="min-h-screen overflow-visible"> {/* Ensure root container doesn't clip megamenu and supports sticky */}
                <GlobalTopBanner text={bannerText} />
                <Header 
                    logoText={logoText} 
                    logoUrl={logoUrl} 
                    menus={headerMenus} 
                    isLoggedIn={isLoggedIn}
                    isBusiness={isBusiness}
                    companyId={companyId}
                />
                <main id="main">
                    {children}
                </main>
                <Footer 
                    companyName={companyName}
                    logoUrl={logoUrl}
                    address={address}
                    phone={phone}
                    email={email}
                    businessNumber={businessNumber}
                    onlineSalesNumber={onlineSalesNumber}
                    hostingProvider={hostingProvider}
                    ceo={ceo}
                    privacyOfficer={privacyOfficer}
                />
            </div>
        </ThemeProvider>
    );
}

export default function App({ children, ...props }: { children?: React.ReactNode; [key: string]: any }) {
    return <Layout {...props}>{children}</Layout>;
}

